import { PurchaseTargetLineStatus, TransferUomOptions } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { TransferTargetLineDTO, TransferTargetDTO } from '@recurrency/core-api-schema/dist/transferOrders/common';
import { SortOrder } from 'antd/lib/table/interface';
import { fuzzyFilter } from 'fuzzbunny';
import moment from 'moment';

import { truthy } from 'utils/boolean';
import { getTenantSetting } from 'utils/tenantSettings';

import { TransferLineUpdates, TransferTargetSummary, UpdatedTransferLine } from 'types/hash-state';

import { DocSummaryType, TransferSummaryLine } from '../PurchaseTargetsPage/types';

// The unit of measure and unit size a line uses for the selected unit option
export function getUnitForOption(line: TransferTargetLineDTO, option: string) {
  switch (option) {
    case TransferUomOptions.Transfer:
      return { uom: line.transferUom, unitSize: line.transferUnitSize };
    case TransferUomOptions.Sales:
      return { uom: line.salesUom, unitSize: line.salesUnitSize };
    case TransferUomOptions.Base:
      return { uom: line.baseUom, unitSize: line.baseUnitSize };
    default:
      return { uom: line.purchasingUom, unitSize: line.purchasingUnitSize };
  }
}

export const getTransferTargetSummaries = (
  updates?: TransferLineUpdates[],
  transferLinesData?: TransferTargetLineDTO[],
  transferTargets?: TransferTargetDTO[],
): TransferTargetSummary[] => {
  const transferSummaries: TransferTargetSummary[] = [];

  transferLinesData?.forEach((line) => {
    const lineUpdates = updates?.find(
      (update) =>
        update.itemId === line.itemId &&
        update.destinationLocationId === line.destinationLocationId &&
        update.sourceLocationId === line.sourceLocationId,
    );
    const qtyToTransfer = lineUpdates?.qtyToTransfer ?? line.defaultTransferQty ?? 0;
    const { uom: unitOfMeasure, unitSize } = getUnitForOption(
      line,
      lineUpdates?.selectedUom ?? TransferUomOptions.Purchasing,
    );

    // This is to make sure we stick to the uoms and not creates TOs for smaller uoms
    const cappedQtyToTransfer =
      Math.floor(Math.max(Math.min(qtyToTransfer, line.qtyTransferable, line.sourceQtyAvailable), 0) / unitSize) *
      unitSize;
    const amountOver = Math.floor((qtyToTransfer - cappedQtyToTransfer) / unitSize) * unitSize;

    const transferSummaryLine: TransferSummaryLine = {
      purchaseLineKey: '', // TODO @brunno-recurrency figure out how to set this
      itemId: line.itemCode,
      itemName: line.itemName,
      itemUid: line.itemId,
      qtyToTransfer: cappedQtyToTransfer,
      unitQuantity: cappedQtyToTransfer / unitSize,
      unitSize,
      unitOfMeasure,
      qtyAvailable: line.sourceQtyAvailable,
    };

    const backorderTransferSummaryLine: TransferSummaryLine = {
      ...transferSummaryLine,
      qtyToTransfer: amountOver,
      unitQuantity: amountOver / unitSize,
    };

    const summaryIndex = transferSummaries.findIndex(
      (summary) =>
        summary.sourceLocation.foreignId === line.sourceLocationId &&
        summary.destinationLocation.foreignId === line.destinationLocationId,
    );

    const transferTarget = transferTargets?.find(
      (target) =>
        target.sourceLocationId === line.sourceLocationId &&
        target.destinationLocationId === line.destinationLocationId,
    );

    // Only add line to summary if there is a quantity to transfer
    if (cappedQtyToTransfer > 0 || amountOver > 0) {
      // If summary object already exists, update it with additional line
      if (summaryIndex >= 0) {
        const summary = transferSummaries[summaryIndex];
        transferSummaries[summaryIndex] = {
          ...summary,
          totalCost: (summary?.totalCost || 0) + (line.unitCost || 0) * cappedQtyToTransfer,
          totalBackorderCost: (summary?.totalBackorderCost || 0) + (line.unitCost || 0) * amountOver,
          totalWeight: (summary?.totalWeight || 0) + line.unitWeight * cappedQtyToTransfer,
          totalBackorderWeight: (summary?.totalBackorderWeight || 0) + line.unitWeight * amountOver,
          totalVolume: (summary?.totalVolume || 0) + line.unitVolume * cappedQtyToTransfer,
          totalBackorderVolume: (summary?.totalBackorderVolume || 0) + line.unitVolume * amountOver,
          totalLines: (summary?.transferLineCount || 0) + 1,
          totalBackorderLines: (summary?.totalBackorderLines || 0) + (amountOver > 0 ? 1 : 0),
          transferLineCount: (summary?.transferLineCount || 0) + (cappedQtyToTransfer > 0 ? 1 : 0),
          lines: [...(summary?.lines || []), ...(cappedQtyToTransfer > 0 ? [transferSummaryLine] : [])],
          backorderLines: [
            ...(summary?.backorderLines || []),
            ...(amountOver > 0 ? [backorderTransferSummaryLine] : []),
          ],
        };
      } else {
        // Otherwise, create a new summary object
        transferSummaries.push({
          kind: DocSummaryType.TransferOrder,
          sourceLocation: { foreignId: line.sourceLocationId, name: line.sourceLocationName },
          destinationLocation: { foreignId: line.destinationLocationId, name: line.destinationLocationName },
          companyId: transferTarget?.companyId ?? '',
          transferDays: transferTarget?.transferDays ?? 0,
          totalCost: line.unitCost * cappedQtyToTransfer,
          totalBackorderCost: line.unitCost * amountOver,
          totalWeight: line.unitWeight * cappedQtyToTransfer,
          totalBackorderWeight: line.unitWeight * amountOver,
          totalVolume: line.unitVolume * cappedQtyToTransfer,
          totalBackorderVolume: line.unitVolume * amountOver,
          totalLines: 1,
          totalBackorderLines: amountOver > 0 ? 1 : 0,
          transferLineCount: cappedQtyToTransfer > 0 ? 1 : 0,
          lines: cappedQtyToTransfer > 0 ? [transferSummaryLine] : [],
          backorderLines: amountOver > 0 ? [backorderTransferSummaryLine] : [],
        });
      }
    }
  });

  return transferSummaries;
};

export function getLineStatusCounts(lineItems: TransferTargetLineDTO[]): Record<PurchaseTargetLineStatus, number> {
  const lineStatusCounts: Obj<number> = {};

  for (const lineItem of lineItems) {
    lineStatusCounts[lineItem.status] = (lineStatusCounts[lineItem.status] ?? 0) + 1;
  }

  return lineStatusCounts as Record<PurchaseTargetLineStatus, number>;
}

// This function sets all current updates to 0 and also adds any lines with non-zero quantity to the updates
export function getResettedTransferLineUpdates(
  transferSummaries: TransferTargetSummary[],
  transferLineUpdates?: TransferLineUpdates[],
) {
  const itemsWithNonZeroQty = [];
  for (const transferSummary of transferSummaries) {
    for (const line of [...transferSummary.lines, ...transferSummary.backorderLines]) {
      if (line.unitQuantity > 0) {
        itemsWithNonZeroQty.push({
          itemId: line.itemUid,
          destinationLocationId: transferSummary.destinationLocation.foreignId,
          sourceLocationId: transferSummary.sourceLocation.foreignId,
        });
      }
    }
  }

  // Combine lines (favoring the transfer line updates which contains the uom selection)
  const combinedUpdates: Record<string, TransferLineUpdates> = {};
  (transferLineUpdates || []).forEach((line) => {
    combinedUpdates[line.itemId] = {
      ...line,
      qtyToTransfer: 0,
    };
  });

  itemsWithNonZeroQty.forEach((item) => {
    if (!combinedUpdates[item.itemId]) {
      combinedUpdates[item.itemId] = {
        itemId: item.itemId,
        destinationLocationId: item.destinationLocationId,
        sourceLocationId: item.sourceLocationId,
        qtyToTransfer: 0,
      };
    }
  });

  return Object.values(combinedUpdates);
}

// find a matching line from the updates with the same item/source/destination
export function findMatchingLineFromUpdates(record: TransferTargetLineDTO, updates?: TransferLineUpdates[]) {
  return updates?.find(
    (line) =>
      line.itemId === record.itemId &&
      line.destinationLocationId === record.destinationLocationId &&
      line.sourceLocationId === record.sourceLocationId,
  );
}

export const convertTransferQtyToUom = (qty: number, unitSize: number) => Math.floor(qty / (unitSize || 1));

export const getQtyInSelectedUom = (record: UpdatedTransferLine, qty: number) => {
  const { selectedUnitSize } = getSelectedUomAndUnitSize(record);
  return convertTransferQtyToUom(qty, selectedUnitSize);
};

export const getTransferQty = (record: UpdatedTransferLine) =>
  getQtyInSelectedUom(record, record.qtyToTransfer ?? record.defaultTransferQty);

export const getDefaultTransferQtyInUom = (record: UpdatedTransferLine) =>
  getQtyInSelectedUom(record, record.defaultTransferQty);

export const getQtyToTbo = (record: UpdatedTransferLine, transferQtyInUom: number) => {
  const qtyRequired = Math.min(record.qtyTransferable, record.sourceQtyAvailable);
  const qtyRequiredInUom = getQtyInSelectedUom(record, qtyRequired);
  return transferQtyInUom - qtyRequiredInUom;
};

export function getSelectedUomAndUnitSize(record: UpdatedTransferLine) {
  const uom = record.selectedUom ?? getTenantSetting(TenantSettingKey.FeatureTransferReplenishmentDefaultUom);
  const { uom: selectedUom, unitSize: selectedUnitSize } = getUnitForOption(
    record,
    uom ?? TransferUomOptions.Purchasing,
  );
  return { selectedUnitSize, selectedUom };
}

// Sort and filter transfer line rows
export function sortAndFilterTransferLineRows(
  transferLineRows: UpdatedTransferLine[],
  sortField: string,
  sortDir: SortOrder,
  searchQuery: string,
): UpdatedTransferLine[] {
  if (transferLineRows.length === 0) return transferLineRows;

  const matchingQueryLines = fuzzyFilter(transferLineRows, searchQuery, {
    fields: ['itemCode', 'itemName'],
  }).map((i) => i.item);

  const sortDirMultiplier = sortDir === 'descend' ? -1 : 1;
  const sortKey = sortField as keyof UpdatedTransferLine;

  // get sort function
  let sortFn: (a: UpdatedTransferLine, b: UpdatedTransferLine) => number;
  if (sortKey === 'defaultTransferQty') {
    sortFn = (a, b) => getDefaultTransferQtyInUom(a) - getDefaultTransferQtyInUom(b);
  } else {
    sortFn = (matchingQueryLines[0] ? typeof matchingQueryLines[0][sortKey] === 'number' : false)
      ? (a: UpdatedTransferLine, b: UpdatedTransferLine) => (a[sortKey] as number) - (b[sortKey] as number)
      : (a: UpdatedTransferLine, b: UpdatedTransferLine) =>
          ((a[sortKey] as string) || '').localeCompare(b[sortKey] as string);
  }

  // sort by field, then itemId asc
  return matchingQueryLines.sort((a, b) => {
    const sortCompare = sortFn(a, b) * sortDirMultiplier;
    if (sortCompare !== 0) return sortCompare;

    const itemIdCompare = a.itemId.localeCompare(b.itemId);
    if (itemIdCompare !== 0) return itemIdCompare;

    return 0;
  });
}

export function getTransferInputKey(record: TransferTargetSummary) {
  return `${record.sourceLocation.foreignId}|${record.destinationLocation.foreignId}`;
}

// Set up to offset the date from UTC to the timezone of the user
export function getOffsetDate(date: string) {
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  return moment(date).add(timezoneOffsetMinutes, 'minutes');
}

export function overwriteTransferLinesForBackorderSplit(
  transferLines: UpdatedTransferLine[],
  summary: TransferTargetSummary,
  backorder: boolean,
): UpdatedTransferLine[] {
  // We need to find the lines that have differing qtyToTransfer than the lines (If they are splitting to TBO and TO)
  // First we find the lines that have a qty above 0, and match the summary of this record
  const nonZeroLines = transferLines.filter((line) => (line.qtyToTransfer || line.originalTransferQty || 0) > 0);

  const summaryLines = nonZeroLines.filter(
    (line) =>
      line.sourceLocationId === summary.sourceLocation.foreignId &&
      line.destinationLocationId === summary.destinationLocation.foreignId,
  );

  // Then we find the lines that match from the summary, and use their quantities instead
  // This ensures that it will show the correct quantities in the modal, instead of the original lines
  const updatedLines = summaryLines.map((line) => {
    const linesFromSummary = backorder ? summary.backorderLines : summary.lines;
    const lineOverrides = linesFromSummary.find((lineFromSummary) => lineFromSummary.itemUid === line.itemId);
    const newQty = lineOverrides?.qtyToTransfer || 0;
    return lineOverrides
      ? {
          ...line,
          qtyToTransfer: newQty,
          extendedCost: newQty * (line.unitCost || 0),
          extendedWeight: newQty * (line.unitWeight || 0),
          extendedVolume: newQty * (line.unitVolume || 0),
        }
      : null;
  });

  return updatedLines.filter(truthy);
}
